"use client";

import React from "react";
import { Button } from "../../../../../components/ui/button";
import {
  Mail,
  MessageCircle,
  Shield,
  Users,
  CreditCard,
  Calendar,
  Camera,
  UserCheck,
  MapPin,
} from "lucide-react";
import { useRouter } from "next/navigation";

const AboutSection = () => {
  const router = useRouter();
  return (
    <section className="flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 gap-8">
      <div className="flex flex-col gap-6 w-full">
        <h1 className="font-bricolage font-bold text-yellow text-4xl sm:text-5xl lg:text-[64px] leading-tight text-center sm:text-left">
          Terms & Conditions
        </h1>

        <div className="prose prose-lg max-w-none">
          <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-6">
            Here is everything you need to know about the terms and conditions
            of booking a trip with us. Please read through carefully and get in
            touch if you need more detail on any point.
          </p>

          <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-8">
            Welcome to Outcome Outing. By booking an
            Outing with us, you agree to the following terms and conditions that
            ensure a smooth, safe, and purpose-driven experience for everyone
            involved.
          </p>

          <div className="space-y-8">
            {/* Section 1 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  1. Eligibility & Community Standards
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                Outcome Outing trips are designed for individuals who seek
                growth through intentional travel. All applicants are reviewed
                to ensure the right mix of energy, mindset, and group fit.
              </p>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                We reserve the right to decline participation if we feel it
                would disrupt the group dynamic or compromise the experience for
                others. We are not trying to exclude — we are trying to curate.
              </p>
            </div>

            {/* Section 2 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <UserCheck className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  2. Application & Confirmation Process
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                Each trip is <strong>invite-only</strong>. Here is how it works:
              </p>
              <ol className="list-decimal list-inside space-y-2 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                <li>You submit your application form via our website.</li>
                <li>Our team reviews your profile within 1–3 working days.</li>
                <li>
                  If shortlisted, you will receive a{" "}
                  <strong>formal invite with payment details</strong>.
                </li>
                <li>
                  To confirm your spot, you must complete a{" "}
                  <strong>50% advance payment</strong> via bank transfer, UPI,
                  or other options we share with you.
                </li>
                <li>
                  The remaining 50% is due 7 days before the trip start date.
                </li>
              </ol>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                If payment is not received within the deadline, your spot may be
                released.
              </p>
            </div>

            {/* Section 3 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <CreditCard className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  3. Payments & Refunds
                </h3>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="font-jakarta font-semibold text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-2">
                    Payment Method:
                  </p>
                  <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                    Payments are collected via UPI, payment gateway, direct
                    transfer, or other secure offline channels. You will receive
                    clear instructions after confirmation.
                  </p>
                </div>
                <div>
                  <p className="font-jakarta font-semibold text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-2">
                    Refunds:
                  </p>
                  <ul className="list-disc list-inside space-y-1 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                    <li>
                      100% refund within 48 hours of booking (if more than 10
                      days before trip).
                    </li>
                    <li>50% refund if cancelled 7–10 days before trip.</li>
                    <li>No refund if cancelled within 7 days of trip start.</li>
                    <li>
                      If the trip is postponed or canceled from our end, a full
                      refund will be offered or the participant can choose to
                      move to the next batch.
                    </li>
                  </ul>
                  <p className="font-jakarta font-semibold text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-2">
                    Processing Time:
                  </p>
                  <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                    Refunds are processed and credited in your account within 7 working days.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  4. Trip Inclusions & Exclusions
                </h3>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="font-jakarta font-semibold text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-2">
                    Your trip typically includes:
                  </p>
                  <ul className="list-disc list-inside space-y-1 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                    <li>Accommodation (shared or private as mentioned)</li>
                    <li>Expert mentor sessions</li>
                    <li>Activities, sightseeing and workshops</li>
                    <li>Select meals (details in itinerary)</li>
                    <li>
                      Group support, travel guide, and internal transport (if
                      applicable)
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-jakarta font-semibold text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-2">
                    Not included unless specified:
                  </p>
                  <ul className="list-disc list-inside space-y-1 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                    <li>Flights or transport to base location</li>
                    <li>Personal expenses, insurance, medical costs</li>
                    <li>Extra meals or extended stays</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  5. Safety, Conduct & Liability
                </h3>
              </div>
              <ul className="list-disc list-inside space-y-2 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                <li>
                  All participants are expected to behave respectfully and
                  responsibly.
                </li>
                <li>
                  Alcohol or substance use is not permitted in a way that
                  disrupts the group.
                </li>
                <li>
                  We are not liable for loss, theft, injury, or damage during
                  the trip.
                </li>
                <li>Personal insurance is not included but recommended.</li>
              </ul>
            </div>

            {/* Section 6 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  6. Women Safety & Inclusivity
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                We take safety seriously — especially for solo women travelers.
                Each trip includes:
              </p>
              <ul className="list-disc list-inside space-y-1 font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-3">
                <li>Verified accommodations</li>
                <li>Trusted facilitators and team members</li>
                <li>Group orientation and check-ins</li>
                <li>Female mentors or buddy systems (where applicable)</li>
              </ul>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                We aim to create safe, respectful, and inclusive environments
                for all genders and identities.
              </p>
            </div>

            {/* Section 7 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Camera className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  7. Media Release & Use of Content
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                During trips, photos and videos may be captured for community
                and marketing use. By participating, you consent to be featured
                unless you explicitly opt out in writing before the trip.
              </p>
            </div>

            {/* Section 8 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  8. Itinerary & Trip Modifications
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed">
                While we strive to stick to planned itineraries, some changes
                may occur due to weather, local conditions, or logistics. Trust
                us — spontaneity is part of the magic.
              </p>
            </div>

            {/* Section 9 */}
            <div className="border-l-4 border-[#ffae00] pl-6">
              <div className="flex items-center gap-3 mb-3">
                <MessageCircle className="w-6 h-6 text-[#ffae00]" />
                <h3 className="font-jakarta font-bold text-[#0D0D0D] text-xl">
                  9. Need Help?
                </h3>
              </div>
              <p className="font-jakarta font-normal text-[#0D0D0D] text-base sm:text-lg leading-relaxed mb-4">
                We are always happy to talk. For support, changes, or payment
                help:
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#ffae00]" />
                  <a
                    href="mailto:info@outcomeouting.com"
                    className="font-jakarta font-medium text-[#ffae00] hover:underline"
                  >
                    info@outcomeouting.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#ffae00]" />
                  <a
                    href="https://wa.me/917982579691"
                    className="font-jakarta font-medium text-[#ffae00] hover:underline"
                  >
                    WhatsApp: +91 7982579691
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center w-full mt-2">
        <Button
          onClick={() => router.push("/trails")}
          variant="outline"
          className="font-jakarta font-semibold text-[#FFAE00] text-lg sm:text-xl tracking-[0.20px] leading-7 px-6 sm:px-7 py-2.5 rounded-xl border border-solid border-[#f1f1f1] bg-transparent hover:bg-[#ffae00]/10 transition-colors duration-200"
        >
          Explore Experiences
        </Button>
      </div>
    </section>
  );
};

export default AboutSection;
